import qs from 'qs';

import type { operations } from '@luxuryescapes/contract-trip/server';

import { request } from './common';

function tripsAdminBasePath() {
  return window.configs.API_HOST + '/api/trips/admin/trips';
}

function tripBasePath() {
  return window.configs.API_HOST + '/api/trips';
}

function getRequest(path: string, options?: { signal?: AbortSignal }) {
  return request(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

function deleteRequest(path: string) {
  return request(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

function postRequest(path: string, body: any) {
  return request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
export async function getTrips({
  page,
  showHiddenOnly,
  tripId,
  orderId,
  cartId,
}: {
  page: number;
  showHiddenOnly?: boolean;
  tripId?: string;
  orderId?: string;
  cartId?: string;
}) {
  // Use qs.stringify to create the query string
  const queryString = qs.stringify({
    page,
    showHiddenOnly,
    tripId,
    orderId,
    cartId,
  });

  const response = await getRequest(tripsAdminBasePath() + `?${queryString}`);
  if (response.status === 200) {
    const tripsWithDebugInfo = await Promise.all(
      response.result.trips.map(async (trip) => {
        try {
          const debugResponse = await generateTripDebugData(trip.id);
          return {
            ...trip,
            hasDebugData: debugResponse.status === 200 && debugResponse.result !== null,
          };
        } catch {
          return {
            ...trip,
            hasDebugData: false,
          };
        }
      }),
    );
    return {
      ...response,
      result: {
        ...response.result,
        trips: tripsWithDebugInfo,
      },
    };
  }

  return response;
}

export interface AdminChatConversationDetails {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface GetChatConversationsResponse {
  status: number;
  result: {
    trips: Array<AdminChatConversationDetails>;
    numberOfTrips: number;
  };
  hasMore?: boolean;
}

export interface GetChatConversationsOptions {
  signal?: AbortSignal;
}

export async function getChatConversations(
  {
    page,
    pageSize,
    chatId,
    userId,
  }: {
    page: number;
    pageSize?: number;
    chatId?: string;
    userId?: string;
  },
  options?: GetChatConversationsOptions,
): Promise<GetChatConversationsResponse> {
  const queryString = qs.stringify({
    page,
    pageSize,
    chatId,
    userId,
  });

  return getRequest(window.configs.API_HOST + `/api/trips/admin/chat-trips?${queryString}`, options);
}

export type ChatTraceResponse = operations['getChatTrace']['responses'][200]['content']['application/json'];
export type ChatTraceEntry = ChatTraceResponse['result']['turns'][number]['entries'][number];
export type ChatTraceAgentStep = Extract<ChatTraceEntry, { itemSeq: number }>;
export type ChatTraceToolExecution = ChatTraceAgentStep['toolExecutions'][number];
export type ChatTraceStoredItem = ChatTraceResponse['result']['unassignedItems'][number];

export async function getChatTrace(
  chatId: string,
  options?: GetChatConversationsOptions & { after?: string },
): Promise<ChatTraceResponse> {
  const query = qs.stringify({ after: options?.after });
  return getRequest(
    `${window.configs.API_HOST}/api/trips/v3/chats/${chatId}/trace${query ? `?${query}` : ''}`,
    options,
  );
}

export async function getTripDetails(tripId: string) {
  return getRequest(tripsAdminBasePath() + `/${tripId}`);
}

export async function generateTripsFromCart(cartId: string) {
  return postRequest(tripBasePath() + `/generate-trip`, { cartId });
}

export async function deleteTripItem(tripId: string, tripItemId: string) {
  return deleteRequest(tripsAdminBasePath() + `/${tripId}/items/${tripItemId}`);
}

export async function generateTripDebugData(tripId: string) {
  return getRequest(tripBasePath() + `/${tripId}/generate-trip-debug`);
}

export interface UserPreference {
  id: string;
  name: string;
  description?: string;
  type: 'THEMES' | 'MUST_DO_ACTIVITY';
  placeId: string;
  locations: Array<string>;
}

export async function getUserPreferences(params?: { type?: 'THEMES' | 'MUST_DO_ACTIVITY'; placeId?: string }) {
  const queryString = qs.stringify(params);
  return postRequest(tripBasePath() + `/get-user-preferences?${queryString}`, params);
}

export async function deleteUserPreference(id: string) {
  return deleteRequest(tripBasePath() + `/delete-user-preference/${id}`);
}
export interface AddUserPreferenceRequest {
  name: string;
  type: 'THEMES' | 'MUST_DO_ACTIVITY';
  description?: string;
  placeId: string;
}

export async function addUserPreference(preference: AddUserPreferenceRequest) {
  return postRequest(tripBasePath() + '/add-user-preference', preference);
}

export interface ActivityLocationImage {
  id: string;
  name: string;
  placeId: string;
  imageIds: Array<string>;
}

export async function getActivityLocationImages(placeId?: string) {
  return getRequest(tripBasePath() + `/activity-location-images/${placeId ?? ''}`);
}

export interface AddActivityLocationImageRequest {
  imageId: string;
  placeId: string;
  location: string;
}

export async function addActivityLocationImage(request: AddActivityLocationImageRequest) {
  return postRequest(tripBasePath() + '/activity-location-image', request);
}

export async function deleteActivityLocationImage(id: string, imageId: string) {
  return deleteRequest(tripBasePath() + `/activity-location-image/${id}/${imageId}`);
}

export async function deleteActivityLocation(id: string) {
  return deleteRequest(tripBasePath() + `/delete-activity-location/${id}`);
}

export interface AddActivityLocationRequest {
  name: string;
  placeId: string;
}

export async function addActivityLocation(request: AddActivityLocationRequest) {
  return postRequest(tripBasePath() + '/add-activity-location', request);
}

export interface Location {
  locationName: string;
  destinationId: string;
  locationImageIds: Array<string>;
  createdAt: string;
  updatedAt: string;
}

export interface GetLocationsParams {
  page?: string;
  locationName?: string;
  destinationId?: string;
}

export async function getLocations(params?: GetLocationsParams) {
  const queryString = params ? qs.stringify(params) : '';
  return getRequest(tripBasePath() + `/locations${queryString ? `?${queryString}` : ''}`);
}

export interface AddLocationRequest {
  destinationId: string;
  imageId: string;
}

export async function addLocation(request: AddLocationRequest) {
  return postRequest(tripBasePath() + '/locations', request);
}

export async function deleteLocationImage(destinationId: string, imageId: string) {
  return request(tripBasePath() + `/locations/${destinationId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageId }),
  });
}
